import { EXPORT_FPS as PREVIEW_FPS, formatFrameTime } from '@/lib/timeline/exportFrameTiming';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, CopyCheck, Trash2, ZoomIn } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useVideoFrameExtractor } from "@/hooks/use-video-frame-extractor";
import {
  DEFAULT_CROP_ZOOM,
  MAX_CROP_ZOOM,
  MIN_CROP_ZOOM,
  getCropZoom,
  type CropCenterKeyframe,
} from "@/hooks/use-auto-save-crop-keyframes";

interface Props {
  keyframe: CropCenterKeyframe;
  sourceAspectRatio?: number;
  videoUrl?: string;
  canDelete: boolean;
  onChange: (next: CropCenterKeyframe) => void;
  onDelete: () => void;
  /** Copy the current x/y/zoom onto every scene. Omitted when there is only one. */
  onApplyToAllScenes?: (framing: Pick<CropCenterKeyframe, "x" | "y" | "zoom">) => void;
  frameWidth?: number;
  /**
   * Optional override for the moment at which to extract the background
   * reference frame. Defaults to the keyframe's start time.
   */
  frameTime?: number;
  previewStartTime?: number;
  previewEndTime?: number;
}

const DEFAULT_FRAME_WIDTH = 320;


const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

const CropCenterEditor: React.FC<Props> = ({
  keyframe,
  sourceAspectRatio = 16 / 9,
  videoUrl,
  canDelete,
  onChange,
  onDelete,
  onApplyToAllScenes,
  frameWidth = DEFAULT_FRAME_WIDTH,
  frameTime,
  previewStartTime = keyframe.time,
  previewEndTime = keyframe.endTime,
}) => {
  const firstFrame = Math.round(previewStartTime * PREVIEW_FPS);
  const lastFrame = Math.max(firstFrame, Math.round(previewEndTime * PREVIEW_FPS) - 1);
  const frameCount = lastFrame - firstFrame + 1;
  const [inspection, setInspection] = useState<{ anchor: number; first: number; last: number; index: number } | null>(null);
  const anchor = frameTime ?? keyframe.time;
  const frameIndex = clamp(inspection?.anchor === anchor && inspection.first === firstFrame && inspection.last === lastFrame
    ? inspection.index : Math.round(anchor * PREVIEW_FPS) - firstFrame, 0, frameCount - 1);
  const extractionTime = (firstFrame + frameIndex) / PREVIEW_FPS;
  const inspectFrame = (index: number) => setInspection({ anchor, first: firstFrame, last: lastFrame, index: clamp(index, 0, frameCount - 1) });
  // An external playhead move replaces a local inspection position.
  useEffect(() => { setInspection(null); }, [anchor, firstFrame, lastFrame]);
  const { t } = useTranslation();
  // Extract at ~2x display resolution for crisp rendering on high-DPI screens.
  const { extractFrame } = useVideoFrameExtractor(videoUrl, {
    canvasWidth: Math.round(frameWidth * 2),
    canvasHeight: Math.round((frameWidth * 2) / (sourceAspectRatio ?? 16 / 9)),
    quality: 0.85,
    maxCachedFrames: 24,
    sampleFps: PREVIEW_FPS,
  });
  const [frame, setFrame] = useState<string | null>(null);
  // Copying to every scene is the one action here that reaches beyond this
  // scene, so it takes a second, explicit click.
  const [confirmApplyAll, setConfirmApplyAll] = useState(false);
  const frameContainerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  // Live crop while the user drags the frame or a slider. Committed to the
  // parent only on pointerup / slider commit so ProjectEditor + Remotion
  // do not re-render on every pixel.
  const [draft, setDraft] = useState<CropCenterKeyframe | null>(null);
  const draftRef = useRef<CropCenterKeyframe | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const displayed = draft ?? keyframe;

  useEffect(() => {
    draftRef.current = null;
    setDraft(null);
  }, [keyframe]);

  const writeDraft = (next: CropCenterKeyframe) => {
    draftRef.current = next;
    setDraft(next);
  };

  const commitDraft = () => {
    const next = draftRef.current;
    if (!next) return;
    onChangeRef.current(next);
  };

  const referenceRequestRef = useRef<{ time: number; extract: typeof extractFrame } | null>(null);
  const extractingRef = useRef(false);
  useEffect(() => {
    const request = { time: extractionTime, extract: extractFrame };
    referenceRequestRef.current = request;
    // Keep only the latest scrub position while a frame is decoding. This
    // bounds work to one active extraction instead of queuing every needle move.
    if (!extractingRef.current) {
      extractingRef.current = true;
      void (async () => {
        try {
          while (referenceRequestRef.current) {
            const next = referenceRequestRef.current;
            const result = await next.extract(next.time).catch(() => null);
            if (referenceRequestRef.current === next) {
              referenceRequestRef.current = null;
              setFrame(result);
            }
          }
        } finally {
          extractingRef.current = false;
        }
      })();
    }
    return () => {
      if (referenceRequestRef.current === request) referenceRequestRef.current = null;
    };
  }, [extractFrame, extractionTime]);

  const frameHeight = Math.round(frameWidth / sourceAspectRatio);

  const zoom = getCropZoom(displayed);

  // 9:16 crop window dimensions as a percentage of the source frame, scaled by zoom.
  // At zoom=1, height fills the source and width is (9/16) / source_aspect.
  // Higher zoom shrinks both axes proportionally; clamped to source bounds.
  const baseCropWidthPct = Math.min(100, ((9 / 16) / sourceAspectRatio) * 100);
  const baseCropHeightPct = 100;
  const cropWindowWidthPct = Math.min(100, baseCropWidthPct / zoom);
  const cropWindowHeightPct = Math.min(100, baseCropHeightPct / zoom);

  const updateFromClientPoint = useCallback(
    (clientX: number, clientY: number) => {
      const el = frameContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
      const y = clamp(((clientY - rect.top) / rect.height) * 100, 0, 100);
      writeDraft({ ...(draftRef.current ?? keyframe), x, y });
    },
    [keyframe],
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    isDraggingRef.current = true;
    updateFromClientPoint(e.clientX, e.clientY);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    updateFromClientPoint(e.clientX, e.clientY);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    isDraggingRef.current = false;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    commitDraft();
  };

  // Keep crop-window rectangle clamped so it never falls outside the frame
  const cropLeftPct = clamp(
    displayed.x - cropWindowWidthPct / 2,
    0,
    100 - cropWindowWidthPct,
  );
  const cropTopPct = clamp(
    displayed.y - cropWindowHeightPct / 2,
    0,
    100 - cropWindowHeightPct,
  );

  return (
    <div className="flex flex-col gap-3" style={{ width: frameWidth }}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("cropCenter.thisSceneOnly")}
          </span>
          <span className="font-mono text-sm font-medium tabular-nums">
            {formatTime(previewStartTime)}
            <span className="text-muted-foreground"> – </span>
            {formatTime(previewEndTime)}
          </span>
        </div>
        {canDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={onDelete}
            title={t("cropCenter.delete")}
            aria-label={t("cropCenter.delete")}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <div
        ref={frameContainerRef}
        className="relative overflow-hidden rounded-md border border-border bg-zinc-950 select-none touch-none cursor-crosshair"
        style={{ width: frameWidth, height: frameHeight }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {frame ? (
          <img
            src={frame}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-80"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-3 w-3 rounded-full border border-primary/60 border-t-transparent animate-spin" />
          </div>
        )}

        <div
          className="pointer-events-none absolute border-2 border-primary/90 rounded-sm"
          style={{
            left: `${cropLeftPct}%`,
            top: `${cropTopPct}%`,
            width: `${cropWindowWidthPct}%`,
            height: `${cropWindowHeightPct}%`,
            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.5), 0 0 0 9999px rgba(0,0,0,0.35)",
          }}
        />

        <div
          className="pointer-events-none absolute h-3 w-3 rounded-full bg-primary ring-2 ring-zinc-950"
          style={{
            left: `calc(${displayed.x}% - 6px)`,
            top: `calc(${displayed.y}% - 6px)`,
          }}
        />
      </div>

      <div className="space-y-1.5 border-b border-border/60 pb-3">
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{t("cropCenter.sceneTime")}</span>
          <span className="font-mono tabular-nums text-foreground">
            {formatTime(extractionTime)}.{Math.floor((extractionTime % 1) * 1000).toString().padStart(3, "0")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0"
            aria-label={t("cropCenter.previousFrame")} title={t("cropCenter.previousFrame")}
            disabled={frameIndex === 0} onClick={() => inspectFrame(frameIndex - 1)}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Slider value={[frameIndex]} min={0} max={Math.max(1, frameCount - 1)} step={1}
            disabled={frameCount <= 1} onValueChange={([index]) => inspectFrame(index)} className="flex-1"
            thumbProps={{ "aria-label": t("cropCenter.sceneTime"), "aria-valuetext": t("cropCenter.frameNumber", { number: frameIndex + 1, total: frameCount }) }} />
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0"
            aria-label={t("cropCenter.nextFrame")} title={t("cropCenter.nextFrame")}
            disabled={frameIndex === frameCount - 1} onClick={() => inspectFrame(frameIndex + 1)}>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="text-center text-[10px] tabular-nums text-muted-foreground">
          {formatFrameTime(extractionTime)} · {t("cropCenter.frameNumber", { number: frameIndex + 1, total: frameCount })}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground w-6">
            X
          </span>
          <Slider
            value={[displayed.x]}
            onValueChange={(v) => writeDraft({ ...(draftRef.current ?? keyframe), x: v[0] })}
            onValueCommit={commitDraft}
            min={0}
            max={100}
            step={0.5}
            className="flex-1"
          />
          <span className="font-mono text-xs tabular-nums w-10 text-right">
            {Math.round(displayed.x)}%
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground w-6">
            Y
          </span>
          <Slider
            value={[displayed.y]}
            onValueChange={(v) => writeDraft({ ...(draftRef.current ?? keyframe), y: v[0] })}
            onValueCommit={commitDraft}
            min={0}
            max={100}
            step={0.5}
            className="flex-1"
          />
          <span className="font-mono text-xs tabular-nums w-10 text-right">
            {Math.round(displayed.y)}%
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 pt-1 border-t border-border/60">
          <span className="flex items-center justify-center w-6 text-muted-foreground">
            <ZoomIn className="h-3 w-3" />
          </span>
          <Slider
            value={[zoom]}
            onValueChange={(v) =>
              writeDraft({
                ...(draftRef.current ?? keyframe),
                zoom: v[0] === DEFAULT_CROP_ZOOM ? undefined : v[0],
              })
            }
            onValueCommit={commitDraft}
            min={MIN_CROP_ZOOM}
            max={MAX_CROP_ZOOM}
            step={0.05}
            className="flex-1"
          />
          <span className="font-mono text-xs tabular-nums w-10 text-right">
            {zoom.toFixed(2)}x
          </span>
        </div>
        {onApplyToAllScenes && (confirmApplyAll ? (
          <div className="mt-1 space-y-2 rounded-md border border-border bg-muted/40 p-2">
            <p className="text-[11px] leading-snug text-muted-foreground">
              {t("cropCenter.applyToAllScenesConfirm")}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 flex-1 text-xs"
                data-testid="crop-apply-all-scenes-cancel"
                onClick={() => setConfirmApplyAll(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-7 flex-1 text-xs"
                data-testid="crop-apply-all-scenes-confirm"
                onClick={() => {
                  const source = draftRef.current ?? keyframe;
                  onApplyToAllScenes({ x: source.x, y: source.y, zoom: source.zoom });
                  draftRef.current = null;
                  setDraft(null);
                  setConfirmApplyAll(false);
                }}
              >
                {t("cropCenter.applyToAllScenesAction")}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-1 h-7 w-full gap-1 text-xs text-muted-foreground"
            data-testid="crop-apply-all-scenes"
            onClick={() => setConfirmApplyAll(true)}
          >
            <CopyCheck className="h-3.5 w-3.5" />
            {t("cropCenter.applyToAllScenes")}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default CropCenterEditor;
